import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from "react";
import { FALLBACK_LANG, loadLocale, peekLocale } from "@/i18n/translations";
import {
  interpolate,
  normalizeLang,
  pluralCategoryFor,
  readStoredLang,
  resolveBootLang,
  type Lang,
  type TVars,
} from "@/i18n/locale";
import { supabase } from "@/integrations/supabase/client";

interface LanguageContextType {
  lang: Lang;
  /**
   * Troca o idioma. CARREGA o dicionário e só então comita — ver o corpo da
   * função. Devolve `true` se a troca aconteceu.
   *
   * O retorno existe para o chamador sequenciar o que vem DEPOIS da troca
   * (fechar o menu, analytics, marcar "escolheu explicitamente"). Disparar
   * essas coisas no clique, como antes, diria que a escolha valeu num momento
   * em que ela ainda podia falhar.
   */
  setLang: (lang: Lang) => Promise<boolean>;
  /**
   * O idioma cujo dicionário está baixando AGORA, ou null.
   *
   * Existe porque a troca deixou de ser instantânea: em 4G são 100-300 ms
   * entre o toque e a tela mudar. Sem isto o usuário não vê nada acontecendo,
   * e "nada acontecendo" não parece lento, parece botão quebrado — ele toca de
   * novo, às vezes em outro idioma, e agora são duas trocas em voo.
   */
  pendingLang: Lang | null;
  /**
   * O idioma cuja troca FALHOU (chunk não baixou), ou null.
   *
   * A troca não acontece sem o dicionário, então em caso de falha o app já
   * segue no idioma anterior — a "reversão" é por construção. O que faltaria
   * sem este campo é DIZER isso: o estado morto é o spinner que nunca some.
   */
  failedLang: Lang | null;
  t: (key: string) => string;
  /**
   * Translate + interpolate. Replaces every `{{token}}` in the resolved string
   * with the matching value.
   *
   * Use this instead of `t(key).replace(...)` chains: the chain style forced
   * every call site to know the token spelling, and the codebase drifted into
   * two conventions ({x} and {{x}}) that no test could tell apart. Passing the
   * values as data also lets a translator reorder them freely, which matters
   * for languages that do not follow the Portuguese word order.
   */
  tf: (key: string, vars: TVars) => string;
  /**
   * Translate a countable string. Resolves `<key>.<categoria CLDR do número no
   * idioma ativo>` e interpola `{{count}}` mais quaisquer vars extras.
   *
   * Era `count === 1 ? ".one" : ".many"`. Isso descreve pt/en/nl/es/bg por
   * acaso e QUEBRA o polonês, que tem uma terceira forma justamente em 2, 3 e 4
   * — os contadores mais comuns de qualquer app ("3 dias seguidos", "2 sessões").
   * Quem decide a categoria agora é o CLDR via Intl.PluralRules, então cada
   * idioma novo traz a própria regra sem tocar neste arquivo.
   *
   * Cai em `.other` quando a categoria exata não existe: português e espanhol
   * produzem `many` a partir de 1 000 000, faixa que ninguém escreve chave para.
   */
  tp: (key: string, count: number, vars?: TVars) => string;
}

const identity = (key: string) => key;

const LanguageContext = createContext<LanguageContextType>({
  lang: "EN",
  setLang: async () => false,
  pendingLang: null,
  failedLang: null,
  t: identity,
  tf: identity,
  tp: identity,
});

export const useLanguage = () => useContext(LanguageContext);

// `resolveBootLang` e `readStoredLang` moraram aqui até o code-splitting. Foram
// para locale.ts porque agora src/main.tsx precisa da MESMA resposta — ele usa
// o idioma de boot para decidir qual dicionário baixar antes de renderizar. Duas
// cópias da regra seriam duas respostas possíveis, e a diferença entre elas é
// exatamente o flash de idioma errado no frame 1.

// Persist the active locale to user_profiles.language. Server-side push
// functions (sleep-wakeup-message today; whispers / daily brief / fitness
// next) cannot read localStorage — this column is their ONLY i18n source.
// Fire-and-forget UPDATE (not upsert): pre-onboarding users have no profile
// row yet, so this no-ops harmlessly; the next sync after the profile exists
// (any language switch or app load) lands the value.
async function persistLanguage(lang: Lang): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase
      .from("user_profiles")
      .update({ language: lang })
      .eq("user_id", user.id);
    if (error) console.error("[LanguageProvider] language sync failed:", error);
  } catch (err) {
    console.error("[LanguageProvider] language sync threw:", err);
  }
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Synchronous boot: paints the persisted (or browser-detected) locale on the
  // FIRST frame with no async gate. Unchanged behavior — the inline validation
  // is now shared via readStoredLang().
  // FLASH HONESTY: this is optimistic, not literal zero-flash. In the tiebreaker
  // case (new device + no stored choice + divergent saved preference) the locale
  // switches ONCE, visibly but smoothly, AFTER auth resolves — a deliberate
  // trade-off so the first paint is never blocked on the network.
  const [lang, setLangState] = useState<Lang>(() => resolveBootLang());

  // Did the device already have a stored choice at boot? Lazy init runs once.
  // Drives the tiebreaker: a device that never chose defers to the server
  // preference; a device that did choose is the source of truth for the session.
  const [hadStoredLangAtBoot] = useState<boolean>(() => readStoredLang() !== null);

  const langRef = useRef<Lang>(lang);
  langRef.current = lang;

  const [pendingLang, setPendingLang] = useState<Lang | null>(null);
  const [failedLang, setFailedLang] = useState<Lang | null>(null);
  /**
   * Id da última troca pedida. É ele que decide quem pode escrever no estado
   * quando a promessa volta.
   *
   * Comparar com `pendingLang` não serviria: no caminho de FALHA o pendente já
   * foi limpo, e a comparação descartaria justamente o erro que precisa
   * aparecer. Um contador monotônico responde a pergunta certa — "esta é ainda
   * a troca mais recente?" — nos dois caminhos.
   *
   * Ref e não estado, pela regra da §11 do CLAUDE.md: ler estado corrente de
   * forma síncrona dentro de um callback assíncrono é o que o ref espelhado
   * existe para resolver.
   */
  const pedidoRef = useRef(0);

  // On authenticated load (SIGNED_IN / INITIAL_SESSION), reconcile the on-screen
  // locale with user_profiles.language via ONE select, splitting into:
  //   • TIEBREAKER — adopt the saved server preference (changes the UI) ONLY for
  //     a device that had no boot-time choice; never overrides a device choice.
  //   • BACKFILL   — when the column is NULL, write the effective UI locale so
  //     server-side push functions (comeback-push-dispatch, sleep-wakeup,
  //     mirror-reminders) stop defaulting to PT-BR. Does NOT touch the UI.
  // The old unconditional persistLanguage(langRef.current) here was the
  // overwrite bug (it clobbered the saved preference on every login) — removed.
  useEffect(() => {
    async function resolveFromProfile(userId: string) {
      const storedBefore = readStoredLang(); // race baseline

      let saved: Lang | null = null;
      try {
        const res = await supabase
          .from("user_profiles")
          .select("language")
          .eq("user_id", userId)
          .maybeSingle();
        if (res.error) {
          console.error("[LanguageProvider] profile language read failed:", res.error);
          return;
        }
        const raw = (res.data as { language?: string | null } | null)?.language;
        saved = normalizeLang(raw);
      } catch (err) {
        console.error("[LanguageProvider] profile language read threw:", err);
        return;
      }

      // RACE GUARD: the user hit the selector during the await → their conscious
      // choice is already in localStorage (and setLang persisted the column).
      // Respect it; abort both branches.
      if (readStoredLang() !== storedBefore) return;

      // ── RAMO 1: column holds a conscious value → NEVER write on login ──────
      if (saved) {
        if (
          !hadStoredLangAtBoot && // device had no stored choice at boot
          !readStoredLang() &&    // and still none (no manual pick mid-load)
          saved !== langRef.current // and it actually diverges from the screen
        ) {
          // TIEBREAKER: adopt the server preference. Lands AFTER auth resolves,
          // never on the first frame — see flash note above.
          //
          // Carrega o dicionário ANTES de trocar, pelo mesmo motivo do setLang:
          // adotar o idioma do servidor sem ele em memória trocaria uma tela
          // correta por uma tela de chaves cruas. Se o chunk falhar, o desempate
          // simplesmente não acontece e o aparelho segue no idioma que já
          // estava — degradação que o usuário nem percebe.
          try {
            await loadLocale(saved);
          } catch {
            return; // loadLocale já logou
          }
          if (readStoredLang() !== storedBefore) return; // re-checa a corrida
          localStorage.setItem("vyv-lang", saved); // next boot is synchronous
          setLangState(saved);
        }
        // else: device wins the session (or already matches) → touch nothing.
        return;
      }

      // ── RAMO 2: sem valor no servidor ────────────────────────────────────
      // Chegar aqui hoje significa UMA de duas coisas, e nenhuma delas é
      // "coluna NULL": a migration de 29/07 pôs NOT NULL + DEFAULT 'PT-BR' +
      // gatilho, então a coluna não pode mais ser nula. Ou a linha de perfil
      // ainda não existe (usuário autenticado que não completou o onboarding —
      // e nesse caso quem carimba o idioma detectado é
      // src/lib/profileLanguage.ts, no momento em que a linha nasce), ou a
      // coluna guarda um valor que normalizeLang não reconhece.
      //
      // O UPDATE condicional que existia aqui (`.is("language", null)`) virou
      // código morto no dia da migration: ele afeta 0 linhas por construção.
      // Mantê-lo daria a impressão de que existe um backfill em execução —
      // exatamente a leitura errada que fez a decisão nº 11 parecer resolvida
      // enquanto 416 linhas continuavam presas no default.
      //
      // A única coisa útil que resta é tornar o próximo boot síncrono.
      if (!readStoredLang()) {
        localStorage.setItem("vyv-lang", langRef.current);
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session?.user) return;
      if (event !== "SIGNED_IN" && event !== "INITIAL_SESSION") return;
      void resolveFromProfile(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, [hadStoredLangAtBoot]);

  // A troca de idioma espera o dicionário CHEGAR antes de comitar o estado.
  //
  // A ordem importa e é o oposto do intuitivo: trocar o estado primeiro e
  // carregar depois pintaria uma tela inteira de chaves cruas no intervalo —
  // pior que o flash de idioma errado, porque nem texto é. Esperando, a tela
  // fica no idioma antigo por ~100-300 ms em 4G e então troca de uma vez.
  //
  // A assinatura continua devolvendo `void`: os call sites (LanguageSelector,
  // Welcome, onboarding, Landing) chamam de dentro de onClick e não esperam
  // retorno. Se o chunk falhar, NADA muda — nem estado, nem localStorage, nem
  // a coluna do perfil — e o erro vai para o console (logo, para o Sentry).
  // A troca de idioma espera o dicionário CHEGAR antes de comitar o estado.
  //
  // A ordem importa e é o oposto do intuitivo: trocar o estado primeiro e
  // carregar depois pintaria uma tela inteira de chaves cruas no intervalo —
  // pior que o flash de idioma errado, porque nem texto é. Esperando, a tela
  // fica no idioma antigo por ~100-300 ms em 4G e então troca de uma vez.
  //
  // Quem conta isso ao usuário é `pendingLang`; quem conta que deu errado é
  // `failedLang`. Sem os dois, a espera é indistinguível de um botão morto.
  const setLang = useCallback(async (newLang: Lang): Promise<boolean> => {
    // Reescolher o idioma atual só limpa um erro anterior. Sem esta saída, o
    // "tentar de novo" em cima do idioma ativo entraria em pendente à toa.
    if (newLang === langRef.current) {
      setFailedLang(null);
      return true;
    }

    const meuPedido = ++pedidoRef.current;
    setFailedLang(null);
    setPendingLang(newLang);

    try {
      await loadLocale(newLang);
    } catch {
      // loadLocale já fez console.error com o idioma e o erro (logo, Sentry).
      if (pedidoRef.current !== meuPedido) return false;
      setPendingLang(null);
      setFailedLang(newLang);
      return false;
    }

    // Outra troca começou depois desta: aquela manda. Sem esta guarda, tocar
    // DE e depois FR deixaria o app em DE se o chunk alemão chegasse por
    // último — a última escolha do usuário perdendo para a rede.
    if (pedidoRef.current !== meuPedido) return false;

    setPendingLang(null);
    setLangState(newLang);
    localStorage.setItem("vyv-lang", newLang);
    // Keep the server-side copy fresh — otherwise pushes go out in the
    // locale the user had at their LAST preference save, not the current one.
    void persistLanguage(newLang);
    return true;
  }, []);

  // SÍNCRONO, e é o ponto do desenho inteiro. `peekLocale` lê o que já está em
  // memória e nunca espera: o dicionário do idioma de boot foi carregado por
  // src/main.tsx ANTES do createRoot, e uma troca de idioma só comita o estado
  // depois que o chunk novo chegou (ver setLang). Logo, no instante em que este
  // callback roda, `peekLocale(lang)` já tem o dicionário — a não ser no caso
  // declarado abaixo.
  //
  // O `|| key` no fim não é decoração: se alguém montar o provider sem o
  // preload (um teste, uma entrada nova no app), a tela mostra a CHAVE, que é
  // visível e diagnosticável, em vez de string vazia. Ver o teste de primeiro
  // frame em src/test/localeFirstPaint.test.tsx.
  const t = useCallback((key: string): string => {
    return peekLocale(lang)?.[key] || peekLocale(FALLBACK_LANG)?.[key] || key;
  }, [lang]);

  const tf = useCallback((key: string, vars: TVars): string => {
    return interpolate(t(key), vars);
  }, [t]);

  const tp = useCallback((key: string, count: number, vars?: TVars): string => {
    const category = pluralCategoryFor(lang, count);
    const primary = `${key}.${category}`;
    // t() devolve a própria chave quando não acha — é como sabemos que a
    // categoria não foi escrita para este idioma (pt/es com `many`, p.ex.).
    const resolved = t(primary);
    const text = resolved === primary ? t(`${key}.other`) : resolved;
    return interpolate(text, { count, ...vars });
  }, [t, lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, pendingLang, failedLang, t, tf, tp }}>
      {children}
    </LanguageContext.Provider>
  );
};
